import React, { useState, useEffect } from 'react';
import { Field } from 'formik';
import { Card, FormGroup } from 'react-bootstrap';
import Skeleton from 'react-loading-skeleton';
import { useTranslation } from 'react-i18next';
import { colors } from '../theme/colors';
import ImageUploadModal from './ImageUploadModal';
import { urlToFile } from '../utils';

interface ImageUploadSectionProps {
  name: string;
  errors: any;
  touched: any;
  values: any;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void;
  onImageUpload: (imageBlob: any) => Promise<string>;
  aspectRatio?: number;
  cropShape?: 'rect' | 'round';
  previewHeight?: number;
  previewWidth?: number;
}

const IMAGE_UPLOAD_STATUS = {
  UPLOADING: "UPLOADING",
  ERROR: "ERROR"
};

interface MainImageProps {
  imageUrl: string;
  height?: number;
  width?: number;
  errorMessage?: string;
}

const MainImage: React.FC<MainImageProps> = ({ imageUrl, height = 198, width = 330, errorMessage }) => {
  if (imageUrl === "") {
    return <Card className="croppedCover" style={{ border: `1px solid ${colors.stroke}`, backgroundColor: colors.background, height, width }} />;
  } else if (imageUrl === IMAGE_UPLOAD_STATUS.UPLOADING) {
    return <Skeleton height={height} width={width} />;
  } else if (imageUrl === IMAGE_UPLOAD_STATUS.ERROR) {
    return (
      <Card className="croppedCover" style={{ height, width }}>
        <div className="alert alert-danger mt-5" role="alert">
          {errorMessage}
        </div>
      </Card>
    );
  } else {
    return <img className="croppedCover" src={imageUrl} alt="preview" style={{ height, width }} />;
  }
};

const getNestedValue = (obj: any, path: string) => {
  return path.split('.').reduce((o, i) => o?.[i], obj);
};

export const ImageUploadSection: React.FC<ImageUploadSectionProps> = ({
  name,
  errors,
  touched,
  values,
  setFieldValue,
  onImageUpload,
  aspectRatio = 1500 / 900,
  cropShape = 'rect',
  previewHeight = 198,
  previewWidth = 330
}) => {
  const { t } = useTranslation();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [croppedImage, setCroppedImage] = useState('');
  const [uploadedImage, setUploadedImage] = useState(getNestedValue(values, name) || "");
  const [tempImageUrl, setTempImageUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setUploadedImage(getNestedValue(values, name) || "");
  }, [values, name]);

  const handleUploadImage = async (imageBlob: any) => {
    try {
      setUploadedImage(IMAGE_UPLOAD_STATUS.UPLOADING);
      setError(null);
      
      // Convert blob to file
      let imageFile: File;
      try {
        imageFile = await urlToFile(imageBlob, 'image.jpeg', 'image/jpeg');
      } catch (error) {
        console.error('Failed to convert image:', error);
        setUploadedImage(IMAGE_UPLOAD_STATUS.ERROR);
        setError(t('imageUpload.errors.conversionFailed'));
        throw new Error(t('imageUpload.errors.conversionFailed'));
      }

      // Upload file
      let imageURL: string;
      try {
        imageURL = await onImageUpload(imageFile);
      } catch (error) {
        console.error('Failed to upload image:', error);
        setUploadedImage(IMAGE_UPLOAD_STATUS.ERROR);
        if (error instanceof Error) {
          setError(error.message);
          throw error;
        }
        setError(t('imageUpload.errors.uploadFailed'));
        throw new Error(t('imageUpload.errors.uploadFailed'));
      }

      if (!imageURL) {
        setUploadedImage(IMAGE_UPLOAD_STATUS.ERROR);
        setError(t('imageUpload.errors.noUrlReturned'));
        throw new Error(t('imageUpload.errors.noUrlReturned'));
      }

      // Success case
      setUploadedImage(imageURL);
      setFieldValue(name, imageURL);
      setTempImageUrl(null);
      setError(null);
      return imageURL;
    } catch (error) {
      console.error('Unexpected error during image upload:', error);
      setUploadedImage(IMAGE_UPLOAD_STATUS.ERROR);
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError(t('imageUpload.errors.unexpectedError'));
      }
      throw error;
    }
  };

  return (
    <>
      <Field
        name={name}
        render={() => (
          <FormGroup controlId={name}>
            <h6 className={`mt-3 mb-0 ${errors[name] && touched[name] ? 'text-danger' : ''}`}>
              {t(`stampCardModifyScreen.rewardPhoto`)}
            </h6>
            <div className="mb-2">
              <small>{t('productModifyScreen.productPhotoDescription')}</small>
            </div>

            <label className="btn btn-secondary">
              {t(`cardModifyScreen.uploadPhoto`)}
              <input
                type="file"
                id={name}
                hidden
                name="file"
                accept="image/*"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const file = event.target.files?.[0];
                  if (file) {
                    const imageUrl = URL.createObjectURL(file);
                    setTempImageUrl(imageUrl);
                    setShowUploadModal(true);
                  }
                }}
              />
            </label>
          </FormGroup>
        )}
      />

      <div style={{ height: previewHeight }}>
        <MainImage 
          imageUrl={uploadedImage} 
          height={previewHeight}
          width={previewWidth}
          errorMessage={error || t('manageVenuesScreen.imageUploadFailed')}
        />
      </div>

      <ImageUploadModal
        show={showUploadModal}
        imageURL={tempImageUrl || getNestedValue(values, name) || ""}
        aspectRatio={aspectRatio}
        cropShape={cropShape}
        onCroppedImage={(croppedImage) => {
          setCroppedImage(croppedImage);
        }}
        onCancel={() => {
          setShowUploadModal(false);
          if (tempImageUrl) {
            URL.revokeObjectURL(tempImageUrl); // Clean up the temporary URL
            setTempImageUrl(null);
          }
        }}
        onSave={() => {
          setShowUploadModal(false);
          handleUploadImage(croppedImage);
          if (tempImageUrl) {
            URL.revokeObjectURL(tempImageUrl); // Clean up the temporary URL
            setTempImageUrl(null);
          }
        }}
      />
    </>
  );
};

export default ImageUploadSection; 